'use client';

import { AlertTriangle } from 'lucide-react';
import { useCommandAgentExpiredIntegrations } from '@/hooks/use-command-agent-expired-integrations';
import { formatIntegrationList } from '@/utils/command-agent-expired-integrations';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Add `?test_expired_integrations=1` to the command chat URL to preview the banner
 * using ACTIVE integrations (no real EXPIRED rows needed).
 */
export function ExpiredIntegrationsBanner({
    agentId,
    onOpenSettings,
}: {
    agentId: string | null;
    onOpenSettings: () => void;
}) {
    const { expiredNames, isLoading, isTestPreview } =
        useCommandAgentExpiredIntegrations(agentId);

    if (!agentId || isLoading || expiredNames.length === 0) {
        return null;
    }

    return (
        <div
            className={cn(
                'mx-3 my-2.5 flex w-auto min-w-0 items-start gap-3 rounded-xl border px-3 py-2.5 sm:mx-4 sm:px-4',
                'border-amber-500/35 bg-gradient-to-r from-amber-50 to-amber-50/60 text-amber-950',
                'dark:border-amber-500/30 dark:from-amber-950/55 dark:to-amber-950/35 dark:text-amber-50'
            )}
            role="status"
        >
            <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/70 dark:text-amber-300">
                <AlertTriangle className="size-3.5" aria-hidden />
            </div>

            <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold leading-snug">
                    <span>Expired app connection{expiredNames.length > 1 ? 's' : ''}</span>
                    {isTestPreview ? (
                        <span className="rounded-md border border-amber-700/25 bg-background/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900/90 dark:border-amber-400/40 dark:text-amber-100">
                            Test preview
                        </span>
                    ) : null}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-amber-900/90 dark:text-amber-100/90">
                    Reconnect now to keep actions working for{' '}
                    <span className="font-semibold text-foreground">
                        {formatIntegrationList(expiredNames)}
                    </span>
                    .
                </p>
            </div>

            <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 border-amber-700/30 bg-background/90 px-3 text-xs font-semibold hover:bg-background dark:border-amber-400/35"
                onClick={onOpenSettings}
            >
                Reconnect
            </Button>
        </div>
    );
}
